import type { OnInit, Signal } from '@angular/core';
import { Component, computed, inject, Injector, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxCallbackOption, OnyxTableData } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalService,
  OnyxSectionComponent,
  OnyxTableComponent,
  OnyxUploadComponent,
} from '@onyx/angular';
import { cloneDeep } from 'lodash';
import { map } from 'rxjs';
import { DictionaryCode } from '../../../../../common/enums/dictionary-code';
import { getContractorsFormDocumentsColumns } from '../../common/constants/contractors-form-documents-columns';
import type { ContractorDocument } from '../../common/interfaces/contractor-documents';
import type {
  ContractorDocumentsFormGroup,
  ContractorDocumentsModalData,
} from './contractor-documents-modal/contractor-documents-modal.component';
import { ContractorDocumentsModalComponent } from './contractor-documents-modal/contractor-documents-modal.component';

export type ContractorDocumentsFormArray = ReturnType<
  typeof ContractorDocumentsFormComponent.buildForm
>;

@Component({
  selector: 'app-contractor-documents-form',
  imports: [
    OnyxSectionComponent,
    OnyxButtonComponent,
    OnyxTableComponent,
    OnyxUploadComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
  templateUrl: './contractor-documents-form.component.html',
  styleUrl: './contractor-documents-form.component.scss',
})
export class ContractorDocumentsFormComponent implements OnInit {
  private readonly modalService = inject(OnyxModalService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly injector = inject(Injector);

  public readonly form = input.required<ContractorDocumentsFormArray>();
  public readonly mode = input.required<OnyxFormMode>();

  protected readonly I18N = 'contractors.contractorForm.documents';

  protected readonly OnyxFormMode = OnyxFormMode;
  protected readonly DictionaryCode = DictionaryCode;

  protected columns = computed(() =>
    getContractorsFormDocumentsColumns(this.mode()),
  );

  protected documents!: Signal<ContractorDocument[] | null>;

  public ngOnInit(): void {
    this.documents = toSignal(
      this.form().valueChanges.pipe(map(() => this.form().getRawValue())),
      {
        initialValue: this.form().getRawValue(),
        injector: this.injector,
      },
    );
  }

  protected getDocumentOptions = (
    data: OnyxTableData<ContractorDocument>,
  ): OnyxCallbackOption[] => {
    return [
      {
        name: 'labels.edit',
        value: () => this.editDocument(data.index),
      },
      {
        name: 'labels.delete',
        value: () => this.removeDocument(data.index),
      },
    ];
  };

  protected addDocument(): void {
    this.openDocumentsModal({
      mode: OnyxFormMode.ADD,
      documents: ContractorDocumentsModalComponent.buildForm(this.fb),
    });
  }

  protected editDocument(index: number): void {
    this.openDocumentsModal({
      mode: OnyxFormMode.EDIT,
      documents: cloneDeep(this.form().at(index)),
      index,
    });
  }

  protected removeDocument(index: number): void {
    this.form().removeAt(index);
  }

  private openDocumentsModal(data: ContractorDocumentsModalData): void {
    this.modalService
      .open<ContractorDocumentsModalData, ContractorDocumentsFormGroup>(
        ContractorDocumentsModalComponent,
        data,
      )
      .result.subscribe((document) => {
        if (!document) return;

        if (data.mode === OnyxFormMode.ADD) {
          this.form().push(document);
        } else {
          this.form().at(data.index).setValue(document.getRawValue());
        }
      });
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.array<ContractorDocumentsFormGroup>([]);
  }
}
